"use client";

import { CSSProperties } from "react";

import { isValidPhoneNumber } from "libphonenumber-js";
import { useTranslations } from "next-intl";
import { PhoneInput } from "react-international-phone";

import IconPhone from "@/icons/call.svg";
import { useCountryStore } from "@/store/country.store";
import { cn } from "@/utils/cn";

import styles from "./style.module.scss";

interface Props {
  error?: string;
  returnedValue?: (params: { phone: string; isValidPhone: boolean }) => void;
  inputStyle?: CSSProperties;
  inputCustomClassName?: string;
  value: string;
  withIcon?: boolean;
  withoutInnerValidator?: boolean;
  variant?: "default" | "separated"; // switch layouts
  showLabels?: boolean; // optional labels for "separated"
  className?: string;
  label?: string;
  required?: boolean;
  disableDialCodePrefill?: boolean;
  placeholder?: string;
}

const isPhoneValid = (phone: string) => {
  try {
    return isValidPhoneNumber(phone);
  } catch {
    return false;
  }
};

export const PhoneInputComponent = ({
  returnedValue,
  error,
  inputCustomClassName,
  value,
  withIcon = false,
  withoutInnerValidator = false,
  variant = "default",
  showLabels = false,
  className,
  label,
  required = false,
  disableDialCodePrefill = false,
  placeholder,
}: Props) => {
  const { country } = useCountryStore();
  const t = useTranslations();

  const handleChange = (val: string) => {
    const cleanedVal = val.replace(/[^\d+]/g, "");
    if (returnedValue) {
      returnedValue({
        phone: cleanedVal,
        isValidPhone: withoutInnerValidator ? true : isPhoneValid(cleanedVal),
      });
    }
  };

  return (
    <div
      className={cn("form-wrapper", error && "form-wrapper-error", className)}
    >
      {label && (
        <div className="form-wrapper-label-container">
          <label
            suppressHydrationWarning
            className={cn(
              "form-wrapper-label",
              error && "form-wrapper-label-error",
            )}
          >
            {label}
            {required && (
              <span
                suppressHydrationWarning
                className="form-wrapper-required"
              >
                *
              </span>
            )}
          </label>
        </div>
      )}
      <div className="relative w-full">
        {/* Separated labels */}
        {variant === "separated" && showLabels && (
          <div className="grid grid-cols-[124px_1fr] gap-5 mb-2">
            <p className="font-medium">{t("search.phone.code")}</p>
            <p className="font-medium">{t("search.phone.number")}</p>
          </div>
        )}

        <PhoneInput
          disableDialCodePrefill={disableDialCodePrefill}
          defaultCountry={country.code?.toLowerCase() || ""}
          placeholder={placeholder}
          className={cn(
            variant === "default"
              ? cn(
                  styles.phoneCustomContainer,
                  inputCustomClassName?.includes("border-none") &&
                    styles.compact,
                )
              : "relative w-full gap-4",
          )}
          inputClassName={cn(
            variant === "default" ? styles.input : "w-full",
            inputCustomClassName,
          )}
          countrySelectorStyleProps={
            variant === "default"
              ? {
                  buttonClassName: styles.btnSelector,
                }
              : {
                  buttonContentWrapperClassName:
                    "h-full w-[120px] border-1 px-4 rounded-lg border-[#ECECEC] flex justify-between bg-white",
                  buttonClassName: "h-full !m-0",
                }
          }
          dialCodePreviewStyleProps={
            variant === "separated"
              ? {
                  className:
                    "border-none h-full bg-transparent pointer-events-none absolute top-0 left-16 text-[#787878] text-md",
                }
              : undefined
          }
          value={value}
          onChange={handleChange}
          disableDialCodeAndPrefix={variant === "separated"}
          showDisabledDialCodeAndPrefix={variant === "separated"}
        />

        {withIcon && (
          <IconPhone className="icon absolute right-[1.4rem] top-1/2 -translate-y-1/2 text-[#545454]" />
        )}

        {error && (
          <div className="absolute text-[#f1334c] text-[1.1rem] py-0.5 px-4">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
